import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { CAP, useSession } from "@/auth/session";
import { Chip } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { PageHead } from "@/components/PageHead";
import { SearchInput } from "@/components/inputs";
import { products, type SpaceRow } from "@/api/client";
import { qk, useSpaces } from "@/modules/products/data";
import { ChoosePrompt, ProductSelect, ScopeBar, ScopeField, ScopeAction } from "@/modules/products/ScopeBar";
import { useKindParam } from "@/lib/hooks/useKindParam";

/** Spaces — a product's partitions (Engineering, Board Production, Repair).
 *
 *  NO MODAL. Dattatray: *"Dont open a pop up for Add space. We need to keep
 *  the UI very similar how we manage Add organisation."* So "Add space"
 *  reveals a row inline, above the table it adds to, and the product comes
 *  from the scope bar rather than being asked for again.
 *
 *  AUTHORING IS SUPER ADMIN ONLY. `CAP.MANAGE_PRODUCTS` is held by no plan
 *  and no company role, so a customer sees the list and no controls. The
 *  server refuses independently — hiding a control is a courtesy, not a
 *  permission check.
 *
 *  THE SLUG IS NOT EDITABLE once created, and that is a refusal with a
 *  reason: a tool SENDS the slug, so re-pointing it means a bench asks for
 *  `eol` and silently gets nothing back. Archive and add instead.
 *
 *  NOT BUILT YET: allocating a space to a company. Dattatray raised it, and
 *  it waits on the role model — today Orbit has three roles and per-user
 *  space access (Fabric's shape), and he parked richer roles (Operations,
 *  Production Engineer, Repairs) as its own decision. `allocations` below is
 *  the real count from `company_product_spaces`, so the number is honest
 *  even though nothing here changes it yet.
 */
export function SpacesPage() {
  const { can } = useSession();
  const mayAuthor = can(CAP.MANAGE_PRODUCTS);
  const [kind, setKind] = useKindParam();   // ?kind= arrives from the Products drawer
  const [adding, setAdding] = useState(false);
  const q = useSpaces(kind);
  const qc = useQueryClient();

  const archive = useMutation({
    mutationFn: (s: SpaceRow) =>
      products.updateSpace(kind, s.space_id, { archived: !s.archived }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.spaces(kind) }),
  });

  const cols: DataCol<SpaceRow>[] = [
    { key: "name", label: "Space", width: 200, render: (s) => s.name },
    { key: "slug", label: "Slug", width: 170, mono: true, render: (s) => s.slug },
    {
      key: "state", label: "State", width: 120,
      render: (s) => s.archived
        ? <Chip tone="mute">archived</Chip>
        : <Chip tone="ok">active</Chip>,
    },
    {
      key: "alloc", label: "Allocated to", width: 140,
      render: (s) => `${s.allocations} ${s.allocations === 1 ? "company" : "companies"}`,
    },
    {
      key: "perms", label: "Permission defaults", width: 160,
      render: (s) => s.permission_defaults,
    },
    ...(mayAuthor ? [{
      key: "act", label: "", width: 110,
      render: (s: SpaceRow) => (
        <button className="btn ghost sm" disabled={archive.isPending}
          onClick={() => archive.mutate(s)}>
          {s.archived ? "Restore" : "Archive"}
        </button>
      ),
    }] : []),
  ];

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Products · Spaces"
        title="Spaces"
        desc="A product's partitions. Each carries its own permission defaults, and a company is allocated the ones it works in."
      />

      <ScopeBar right={mayAuthor ? (
        <button className={adding ? "btn ghost sm" : "btn teal sm"}
          disabled={!kind} onClick={() => setAdding(!adding)}>
          {adding ? "Cancel" : "Add space"}
        </button>
      ) : undefined}>
        <ProductSelect value={kind} onChange={setKind} />
      </ScopeBar>

      {adding && <AddSpaceRow kind={kind} onDone={() => setAdding(false)} />}

      {archive.isError && (
        <div className="panel panel-pad" style={{ marginBottom: 10 }}>
          {(archive.error as Error).message}
        </div>
      )}

      {!kind ? <ChoosePrompt what="spaces" /> : (
        <DataTable rows={q.data?.spaces ?? []} cols={cols} rowKey={(s) => s.space_id}
          empty={<div className="panel panel-pad cap">
            {q.isLoading ? "Loading…" : "No spaces defined for this product yet."}
          </div>} />
      )}
    </div>
  );
}

function AddSpaceRow({ kind, onDone }: { kind: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const add = useMutation({
    mutationFn: () => products.addSpace(kind, { slug, name, description: description || null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.spaces(kind) });
      void qc.invalidateQueries({ queryKey: qk.products });
      onDone();
    },
  });

  return (
    <div className="panel panel-pad" style={{
      display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap",
      marginBottom: 12,
    }}>
      <ScopeField label="Slug" hint="what the tool sends — permanent">
        <SearchInput value={slug} autoFocus mono placeholder="board_production"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSlug(e.target.value.toLowerCase().replace(/\s+/g, "_"))} />
      </ScopeField>
      <ScopeField label="Name" hint="what a person reads">
        <SearchInput value={name} placeholder="Board Production"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
      </ScopeField>
      <ScopeField label="Description" hint="optional">
        <SearchInput value={description}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)} />
      </ScopeField>
      <ScopeAction>
        <button className="btn teal sm" disabled={!slug || !name || add.isPending}
          onClick={() => add.mutate()}>
          {add.isPending ? "Adding…" : "Add space"}
        </button>
      </ScopeAction>
      {add.isError && (
        <div className="panel panel-pad" style={{ flexBasis: "100%" }}>
          {(add.error as Error).message}
        </div>
      )}
    </div>
  );
}
